import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

const TenantContext = createContext(null)

const STORAGE_KEY = 'wt_current_tenant'

export function TenantProvider({ children }) {
  const queryClient = useQueryClient()
  const [currentTenantId, setCurrentTenantId] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? parseInt(stored, 10) : null
  })

  const { data: tenantsData, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => {
      const token = localStorage.getItem('qora_auth_token')
      const headers = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      return fetch('/api/tenants', { headers, credentials: 'include' }).then(r => r.json())
    },
    staleTime: 60000
  })

  const tenants = tenantsData?.tenants || []
  const masterTenant = tenants.find(t => t.is_master)
  const currentTenant = tenants.find(t => t.id === currentTenantId) || masterTenant || tenants[0]
  
  const isTenantReady = !isLoading && !!currentTenant?.id

  useEffect(() => {
    if (currentTenant && currentTenant.id !== currentTenantId) {
      setCurrentTenantId(currentTenant.id)
      localStorage.setItem(STORAGE_KEY, currentTenant.id.toString())
    }
  }, [currentTenant, currentTenantId])

  const switchTenant = (tenantId) => {
    setCurrentTenantId(tenantId)
    localStorage.setItem(STORAGE_KEY, tenantId.toString())
    queryClient.invalidateQueries()
  }

  const tenantFetch = useCallback(async (url, options = {}) => {
    if (!currentTenant?.id) {
      console.warn('[TenantContext] tenantFetch called before tenant is ready:', url)
      return Promise.reject(new Error('Tenant not ready'))
    }
    const token = localStorage.getItem('qora_auth_token')
    const headers = {
      ...options.headers,
      'X-Tenant-ID': currentTenant.id.toString(),
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return fetch(url, { ...options, headers, credentials: 'include' })
  }, [currentTenant?.id])

  const isMasterContext = currentTenant?.is_master || false

  const value = {
    tenants,
    currentTenant,
    currentTenantId: currentTenant?.id,
    masterTenant,
    isMasterContext,
    isLoading,
    isTenantReady,
    switchTenant,
    tenantFetch
  }

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}

export default TenantContext
